import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus, Minus, X, HardHat, CheckCircle, Users, AlertCircle, Trash2, Clock, CalendarClock, ChevronDown, ChevronUp, Check } from 'lucide-react';
import { haritala, konumYolu } from '@/lib/konum';
import { toast } from 'sonner';
import KonumSecici from '@/components/insaat/KonumSecici';
import PozSecici from '@/components/insaat/PozSecici';
import MasrafGrubuSecici from '@/components/insaat/MasrafGrubuSecici';
import IsTablosu, { POZISYONLAR, kisiSayisi } from '@/components/insaat/IsTablosu';

// Bu sayfa kimlik doğrulaması GEREKTİRMEZ — alt yüklenici linki token ile
// çözülür. Kasıtlı olarak `lib/api.js`'teki JWT'li istemciyi KULLANMIYORUZ;
// birinin ileride o ortak istemciye "yetki gerekli" mantığı eklemesi bu
// sayfayı sessizce kırmasın diye ayrı, düz `fetch` kullanılıyor.
//
// MOBİL ÖNCELİKLİ (kullanıcı, 2026-10-21): sahada telefondan doldurulur.
// Dokunma alanları ≥44px, yazı 16px (iOS odaklanınca büyütmesin), tek ana
// buton alta yapışık. Her İŞ bir kart; o işte çalışan EKİP (pozisyon + kişi
// sayısı) kartın içinde girilir — puantaj ayrı girilmez, sunucu işlerin
// ekibinden üretir (konum ve "kimin işçisi" işten gelir).
const API_BASE = window.location.origin + '/api/erp/insaat';

const bugun = () => new Date().toLocaleDateString('en-CA');
const uid = () => Math.random().toString(36).slice(2, 10);
const tarihMetni = (iso) => (iso ? new Date(`${iso}T00:00:00`).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long' }) : '');

// İŞ TAKİBİ (kullanıcı, 2026-11-03): bir iş birkaç gün sürebilir, metrajı
// bitince çıkar. "Bu iş tamamlandı" işaretlenmeyen iş, ertesi günün formuna
// sunucudan (`devam_eden_isler`) kendiliğinden gelir; aynı `is_id` ile
// günler bağlanır, işin toplam adam·gün / adam·saati ofiste görülür.

// Mobil girdiler: 48px yükseklik, 16px yazı
const ALAN = 'h-12 text-base bg-white border-[#d0c4b0]';
const ETIKET = 'block text-sm font-medium text-[#3F3E39] mb-1.5';

// MASAÜSTÜ (kullanıcı, 2026-11-03: "bilgisayardan girmek isteyen de
// rahatlıkla girebilsin"): geniş ekranda işler Excel gibi tek tabloda
// (IsTablosu). İlk deneme liste + yapışık yan paneldi; iki ayrı dikey
// kaydırma oluşturduğu için bırakıldı. Tablo ~1340px altında sıkışır, orada
// mobil kart düzeni kullanılır. Düzen JS ile seçilir (aynı satır iki kez
// çizilmesin, odak/kimlikler çakışmasın).
const MASAUSTU = '(min-width: 1340px)';
function useMasaustu() {
  const [m, setM] = useState(() => window.matchMedia(MASAUSTU).matches);
  useEffect(() => {
    const mq = window.matchMedia(MASAUSTU);
    const dinle = (e) => setM(e.matches);
    mq.addEventListener('change', dinle);
    return () => mq.removeEventListener('change', dinle);
  }, []);
  return m;
}

function Stepper({ deger, onChange, disabled, testId }) {
  const n = parseInt(deger, 10) || 0;
  return (
    <div className="flex items-center rounded-lg border border-[#d0c4b0] bg-white overflow-hidden shrink-0">
      <button type="button" disabled={disabled || n <= 0} onClick={() => onChange(Math.max(0, n - 1))}
        aria-label="Bir azalt" className="h-12 w-11 flex items-center justify-center text-[#3F3E39] active:bg-[#efe8dc] disabled:opacity-40">
        <Minus size={18} />
      </button>
      <input type="number" inputMode="numeric" min="0" value={deger} disabled={disabled} data-testid={testId}
        onChange={(e) => onChange(e.target.value)} aria-label="Kişi sayısı"
        className="h-12 w-12 text-center text-base font-semibold tabular-nums border-x border-[#e8dfd0] outline-none" />
      <button type="button" disabled={disabled} onClick={() => onChange(n + 1)}
        aria-label="Bir artır" className="h-12 w-11 flex items-center justify-center text-[#3F3E39] active:bg-[#efe8dc] disabled:opacity-40">
        <Plus size={18} />
      </button>
    </div>
  );
}

export default function PublicDailyReportForm() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [invalid, setInvalid] = useState(false);
  const [subcontractorName, setSubcontractorName] = useState('');
  const [sites, setSites] = useState([]);
  const [konumlar, setKonumlar] = useState([]);   // tüm şantiyelerin seçilebilir konumları
  const [altTaseronlar, setAltTaseronlar] = useState([]);   // ana yüklenicinin (aktif) alt taşeronları
  const [ustTaseronAdi, setUstTaseronAdi] = useState(null); // bu link bir alt taşeronunsa
  const [siteId, setSiteId] = useState('');
  const [reportDate, setReportDate] = useState(bugun());
  const [status, setStatus] = useState(null);
  const [reviewNote, setReviewNote] = useState(null);
  const [isler, setIsler] = useState([]);                 // tutanak satırları + ekip
  const [bagimsizPuantaj, setBagimsizPuantaj] = useState([]);   // eski kayıt: işe bağlı olmayan puantaj
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isKalemleri, setIsKalemleri] = useState([]);    // FİYATSIZ gelir
  const [masrafGruplari, setMasrafGruplari] = useState([]);   // ilave işin iş grubu (kodsuz)
  const [sabitTarih, setSabitTarih] = useState(null);    // WhatsApp hatırlatma linki o GÜNE kilitli
  const [calismaYok, setCalismaYok] = useState(false);
  const [hatalar, setHatalar] = useState({});             // line_id -> mesaj
  const [isBilgileri, setIsBilgileri] = useState({});     // is_id -> {baslangic, gun_no, onceki_miktar…}
  // Aynı anda TEK iş kartı açık; diğerleri tek satırlık özet (kullanıcı,
  // 2026-11-03: iş ekledikçe sayfa çok uzuyordu)
  const [acikIs, setAcikIs] = useState(null);
  const masaustu = useMasaustu();
  const [varsayilanSaat, setVarsayilanSaat] = useState(9);

  // Yalnız token değişince ilk yükleme; tarih/şantiye değişimi kendi işleyicilerinden.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadForm(reportDate); }, [token]);

  // secilenSite: rapor o şantiye için aranır. satirlariKoru: şantiye
  // değiştirilip o şantiyede rapor YOKSA yazılmış işler kaybolmasın — yalnız
  // konumları sıfırlanır. Tarih değişince KORUNMAZ: dünün işleri yanlışlıkla
  // bugüne gönderilmesin.
  const loadForm = async (tarih, secilenSite = null, satirlariKoru = false) => {
    setLoading(true);
    try {
      const q = new URLSearchParams({ tarih });
      if (secilenSite) q.set('site_id', secilenSite);
      const res = await fetch(`${API_BASE}/rapor/${token}?${q}`);
      if (!res.ok) { setInvalid(true); setLoading(false); return; }
      const data = await res.json();
      setSubcontractorName(data.subcontractor_name);
      setSites(data.sites || []);
      setKonumlar(data.konumlar || []);
      setAltTaseronlar(data.alt_taseronlar || []);
      setIsKalemleri(data.is_kalemleri || []);
      setMasrafGruplari(data.masraf_gruplari || []);
      setVarsayilanSaat(data.varsayilan_saat || 9);
      // Devam eden işlerin "kaçıncı gün" bilgisi: yeni raporda kartın kendisinde,
      // var olan raporda sunucunun is_bilgileri'nden
      const bilgi = { ...(data.is_bilgileri || {}) };
      (data.devam_eden_isler || []).forEach((d) => { bilgi[d.is_id] = d; });
      setIsBilgileri(bilgi);
      setUstTaseronAdi(data.ust_taseron_adi || null);
      setReportDate(data.report_date);
      setSabitTarih(data.sabit_tarih || null);
      setHatalar({});
      setAcikIs(null);
      const mevcut = data.existing_report;
      setCalismaYok(Boolean(mevcut?.calisma_yok));
      if (mevcut) {
        setSiteId(mevcut.site_id);
        setStatus(mevcut.status);
        setReviewNote(mevcut.review_note);
        const puantaj = mevcut.puantaj_entries || [];
        setIsler((mevcut.tutanak_items || []).map((r) => ({
          ...r,
          tur: r.kalem_id ? 'poz' : 'ilave',
          miktar: r.miktar ?? '',
          // Yeni kayıtta ekip satırın içinde; eskide işe bağlı puantajdan kurulur
          ekip: (r.ekip?.length ? r.ekip : puantaj.filter((p) => p.tutanak_line_ref === r.line_id))
            .map((e) => ({ id: uid(), pozisyon: e.pozisyon, adet: e.adet })),
        })));
        setBagimsizPuantaj(puantaj.filter((p) => !p.tutanak_line_ref));
      } else {
        setSiteId(secilenSite || data.default_site_id || '');
        setStatus(null);
        setReviewNote(null);
        const devamKartlari = (data.devam_eden_isler || []).map((d) => ({
          line_id: uid(), is_id: d.is_id, tur: d.tur, kalem_id: d.kalem_id, poz_no: d.poz_no,
          is_cinsi: d.is_cinsi || '', birim: d.birim || '', masraf_grubu_id: d.masraf_grubu_id,
          konum_id: d.konum_id, konum_yolu: d.konum_yolu, alt_taseron_id: d.alt_taseron_id,
          miktar: '', saat: d.saat, aciklama: '', tamamlandi: false,
          // Ekip önceki günden GELMEZ: her gün kaç kişi çalıştığı yeniden girilir
          // (kullanıcı, 2026-11-03 — dünkü sayı sorgusuz gönderilmesin)
          ekip: [],
        }));
        // Şantiye değişince yazılmış YENİ işler korunur (konumları sıfırlanır);
        // önceki şantiyenin devam eden işleri bırakılır
        setIsler((p) => [...devamKartlari, ...(satirlariKoru
          ? p.filter((r) => !r.is_id).map((r) => ({ ...r, konum_id: null, konum_yolu: null })) : [])]);
        setBagimsizPuantaj([]);
      }
    } catch (err) {
      setInvalid(true);
    }
    setLoading(false);
  };

  const changeDate = (val) => { setReportDate(val); loadForm(val, siteId || null, false); };
  const changeSite = (val) => loadForm(reportDate, val, true);

  const siteKonumlari = konumlar.filter((k) => k.site_id === siteId);
  // Liste boş olsa da eski bir satır pasif bir alt taşeronu gösteriyorsa seçici
  // görünmeli; yoksa gönderim 400 alır ve düzeltecek yer olmaz.
  const yapanVar = altTaseronlar.length > 0 || isler.some((r) => r.alt_taseron_id);
  const kalemHaritasi = Object.fromEntries(isKalemleri.map((k) => [k.id, k]));
  const kilitli = status === 'approved';
  // "Bugün çalışma yok" yalnız bugün yeni iş girilmemişken anlamlı; önceki
  // günlerden gelen devam işleri varken durur (o gün hiç çalışılmamış olabilir).
  // Bugün başlayan satırda is_id yoktur ya da kendi line_id'sidir.
  const yeniIsVar = isler.some((r) => !r.is_id || r.is_id === r.line_id);

  // Yeni iş bir öncekinin konumunu ve "yapan"ını alır: ekip çoğunlukla aynı
  // yerde, aynı alt taşeronla çalışıyor.
  // İlave işte alt yüklenicinin TEK iş grubu varsa o seçili gelir
  const tekGrup = masrafGruplari.filter((g) => g.kendi);
  const isEkle = (tur) => {
    const yeniId = uid();
    setAcikIs(yeniId);
    setTimeout(() => document.getElementById(`is-${yeniId}`)?.scrollIntoView({ behavior: 'smooth', block: masaustu ? 'nearest' : 'start' }), 60);
    setIsler((prev) => {
      const son = prev[prev.length - 1];
      return [...prev, {
        line_id: yeniId, tur, kalem_id: null, is_cinsi: '', birim: '', miktar: '',
        is_id: null, tamamlandi: false, saat: son?.saat || varsayilanSaat,
        masraf_grubu_id: tur === 'ilave' && tekGrup.length === 1 ? tekGrup[0].id : null,
        konum_id: son?.konum_id || null, alt_taseron_id: son?.alt_taseron_id || null, aciklama: '',
        ekip: [],
      }];
    });
  };
  const isGuncelle = (lineId, alanlar) => {
    setIsler((prev) => prev.map((r) => (r.line_id === lineId ? { ...r, ...alanlar } : r)));
    setHatalar((h) => { if (!h[lineId]) return h; const y = { ...h }; delete y[lineId]; return y; });
  };
  const isSil = (lineId) => {
    const r = isler.find((x) => x.line_id === lineId);
    const devamMi = r?.is_id && isBilgileri[r.is_id];
    if (devamMi && !window.confirm('Bu iş bugün yapılmadı mı? Kart bugünden kaldırılır; iş açık kalır ve yarın tekrar gelir.')) return;
    if (!devamMi && (r?.kalem_id || r?.is_cinsi || r?.ekip?.length) && !window.confirm('Bu iş silinsin mi?')) return;
    setIsler((prev) => prev.filter((x) => x.line_id !== lineId));
  };
  const ekipGuncelle = (lineId, fn) => {
    const r = isler.find((x) => x.line_id === lineId);
    isGuncelle(lineId, { ekip: fn(r.ekip || []) });
  };
  // Hızlı pozisyon: varsa bir artır, yoksa 1 kişiyle ekle
  const pozisyonEkle = (lineId, pozisyon) => ekipGuncelle(lineId, (ekip) => {
    const var_ = ekip.find((e) => e.pozisyon === pozisyon);
    return var_ ? ekip.map((e) => (e === var_ ? { ...e, adet: (parseInt(e.adet, 10) || 0) + 1 } : e))
      : [...ekip, { id: uid(), pozisyon, adet: 1 }];
  });

  const dogrula = () => {
    const h = {};
    isler.forEach((r) => {
      const devam = r.is_id ? isBilgileri[r.is_id] : null;
      const oncekiMiktar = devam?.onceki_miktar || 0;
      if (r.tur === 'poz' && !r.kalem_id) h[r.line_id] = 'İş kalemini seçin';
      else if (r.tur !== 'poz' && !(r.is_cinsi || '').trim()) h[r.line_id] = 'İş tanımını yazın';
      else if (r.tur !== 'poz' && !r.masraf_grubu_id) h[r.line_id] = 'İşin grubunu seçin';
      // Metraj iş bitince: yalnız TAMAMLANAN sözleşmeli işte (önceki günlerde de girilmemişse) şart
      else if (r.tur === 'poz' && r.tamamlandi && !((parseFloat(r.miktar) || 0) + oncekiMiktar > 0)) {
        h[r.line_id] = 'İş tamamlandı — yapılan miktarı girin';
      } else if (parseFloat(r.miktar) < 0) h[r.line_id] = 'Miktar eksi olamaz';
      else if (!(parseFloat(r.saat) > 0 && parseFloat(r.saat) <= 24)) h[r.line_id] = 'Çalışma saatini girin (0–24)';
      else if (devam && r.tamamlandi && !kisiSayisi(r.ekip)) { /* dün bitmiş, bugün yalnız kapatılıyor */ }
      else if (!(r.ekip || []).some((e) => (e.pozisyon || '').trim() && parseInt(e.adet, 10) > 0)) {
        h[r.line_id] = 'Bu işte çalışan ekibi girin (pozisyon ve kişi sayısı)';
      } else if ((r.ekip || []).some((e) => (parseInt(e.adet, 10) > 0) && !(e.pozisyon || '').trim())) {
        h[r.line_id] = 'Ekipte pozisyonu boş satır var';
      }
    });
    return h;
  };

  // calismaYokMu: "Bugün çalışma yok" — satırsız rapor; o gün raporlanmış
  // sayılır, hatırlatma ve şefe bildirim gitmez.
  const handleSubmit = async (calismaYokMu = false) => {
    if (!siteId) { toast.error('Şantiye seçin'); return; }
    if (!calismaYokMu) {
      if (!isler.length) { toast.error('En az bir iş ekleyin ya da "Bugün çalışma yok" deyin'); return; }
      const h = dogrula();
      setHatalar(h);
      const ilk = isler.find((r) => h[r.line_id]);
      if (ilk) {
        toast.error(`${Object.keys(h).length} işte eksik bilgi var`);
        setAcikIs(ilk.line_id);
        document.getElementById(`is-${ilk.line_id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
    }
    if (calismaYokMu && isler.length && !window.confirm(
      'Bu gün "çalışma yok" olarak bildirilecek. Önceki günlerden devam eden işler açık kalır ve yarın formunuza tekrar gelir. Devam edilsin mi?')) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/rapor/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          site_id: siteId,
          report_date: reportDate,
          calisma_yok: calismaYokMu,
          tutanak_items: calismaYokMu ? [] : isler.map((r) => ({
            line_id: r.line_id, is_id: r.is_id || null, tamamlandi: !!r.tamamlandi, saat: r.saat,
            ...(r.tur === 'poz' ? { kalem_id: r.kalem_id }
              : { is_cinsi: r.is_cinsi, birim: r.birim, masraf_grubu_id: r.masraf_grubu_id }),
            miktar: r.miktar, konum_id: r.konum_id, alt_taseron_id: r.alt_taseron_id, aciklama: r.aciklama,
            ekip: (r.ekip || []).filter((e) => (e.pozisyon || '').trim() || parseInt(e.adet, 10) > 0)
              .map((e) => ({ pozisyon: e.pozisyon, adet: e.adet })),
          })),
          // Eski kayıtta işe bağlı olmayan puantaj olduğu gibi korunur
          puantaj_entries: calismaYokMu ? [] : bagimsizPuantaj,
        }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        toast.error(d.detail || (res.status === 409 ? 'Bu gün için rapor onaylanmış, değiştirilemez.' : 'Gönderilemedi'));
      } else {
        setCalismaYok(calismaYokMu);
        if (calismaYokMu) setIsler([]);
        setSubmitted(true);
        window.scrollTo(0, 0);
      }
    } catch (err) {
      toast.error('Bağlantı hatası, tekrar deneyin');
    }
    setSubmitting(false);
  };

  if (loading) {
    return <div className="min-h-dvh flex items-center justify-center bg-[#f5f0e8] text-[#8a7e70]">Yükleniyor…</div>;
  }

  if (invalid) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-[#f5f0e8] px-6">
        <div className="text-center">
          <AlertCircle size={36} className="text-[#7a3535] mx-auto mb-3" />
          <p className="text-lg font-bold text-[#7a3535]">Geçersiz veya süresi dolmuş bağlantı</p>
          <p className="text-base text-[#5c554b] mt-2">Lütfen ofisle iletişime geçin.</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    const toplamKisi = isler.reduce((t, r) => t + kisiSayisi(r.ekip), 0);
    return (
      <div className="min-h-dvh flex items-center justify-center bg-[#f5f0e8] px-6">
        <div className="text-center max-w-sm">
          <CheckCircle size={48} className="text-[#3d6e4e] mx-auto mb-3" />
          <p className="text-xl font-bold text-[#1a1916]">{calismaYok ? 'Bildirildi' : 'Rapor gönderildi'}</p>
          <p className="text-base text-[#5c554b] mt-2">
            {reportDate.split('-').reverse().join('.')}{' '}
            {calismaYok ? 'günü "çalışma yok" olarak kaydedildi.' : `· ${isler.length} iş · ${toplamKisi} kişi kaydedildi.`}
          </p>
          <Button onClick={() => setSubmitted(false)} variant="outline" className="mt-6 h-12 px-6 text-base border-[#d0c4b0]">
            Bu günü tekrar düzenle
          </Button>
        </div>
      </div>
    );
  }

  const toplamKisi = isler.reduce((t, r) => t + kisiSayisi(r.ekip), 0);

  const tarihSantiye = (
    <section className={`rounded-2xl border border-[#d0c4b0] bg-white grid grid-cols-1 gap-4 sm:grid-cols-2
      ${masaustu ? 'p-3 w-[540px] shrink-0' : 'p-4 mb-6'}`}>
      <div>
        <label className={ETIKET} htmlFor="rapor-tarih">Tarih</label>
        <Input id="rapor-tarih" type="date" value={reportDate} max={bugun()} disabled={kilitli || !!sabitTarih}
          onChange={(e) => changeDate(e.target.value)} className={ALAN} data-testid="rapor-tarih" />
        {sabitTarih && <p className="mt-1 text-sm text-[#8a7e70]">Bu bağlantı yalnız bu günün raporu içindir.</p>}
      </div>
      <div>
        <label className={ETIKET} htmlFor="rapor-santiye">Şantiye *</label>
        <select id="rapor-santiye" value={siteId} onChange={(e) => changeSite(e.target.value)} disabled={kilitli}
          className="w-full h-12 px-3 rounded-md border border-[#d0c4b0] bg-white text-base text-[#1a1916] disabled:opacity-60">
          <option value="" disabled>Seçin</option>
          {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </div>
    </section>
  );

  const konumHarita = haritala(siteKonumlari);
  // Kartı "Tamam" ile kapatırken o işin eksiği varsa kart açık kalır, hata altında
  const kartiKapat = (lineId) => {
    const h = dogrula()[lineId];
    if (h) { setHatalar((x) => ({ ...x, [lineId]: h })); return; }
    setAcikIs(null);
    setTimeout(() => document.getElementById(`is-${lineId}`)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 60);
  };

  const ozetSatiri = (r, sira) => {
    const k = kalemHaritasi[r.kalem_id];
    const hata = hatalar[r.line_id];
    const devam = r.is_id ? isBilgileri[r.is_id] : null;
    const birim = r.tur === 'poz' ? (k?.birim || r.birim) : r.birim;
    const baslik = r.tur === 'poz'
      ? (k ? `${k.poz_no} · ${k.ad}` : (r.poz_no ? `${r.poz_no} · ${r.is_cinsi}` : 'Poz seçilmedi'))
      : (r.is_cinsi || 'İş tanımı yazılmadı');
    const yol = r.konum_id ? (konumYolu(r.konum_id, konumHarita) || r.konum_yolu || '') : '';
    const kisaYol = yol ? yol.split(' / ').slice(-2).join(' / ') : (siteKonumlari.length ? 'Şantiye geneli' : '');
    const miktarMetni = parseFloat(r.miktar) > 0 ? `${r.miktar} ${birim || ''}`.trim() : (r.tamamlandi ? '' : 'metraj bitince');
    const meta = [miktarMetni, kisaYol, r.saat ? `${r.saat} saat` : ''].filter(Boolean);
    // Ad KIRPILMAZ: benzer pozları ayıran kısım adın sonunda (ör. "h:6,5-7,5")
    const bosBaslik = (r.tur === 'poz' && !k && !r.poz_no) || (r.tur !== 'poz' && !r.is_cinsi);
    return (
      <section key={r.line_id} id={`is-${r.line_id}`} data-testid={`is-ozet-${sira}`}
        className={`rounded-2xl border bg-white shadow-sm scroll-mt-4 ${hata ? 'border-[#b4442f] ring-1 ring-[#b4442f]/30' : 'border-[#d0c4b0]'}`}>
        <button type="button" onClick={() => setAcikIs(r.line_id)} aria-expanded="false"
          aria-label={`${sira}. iş: ${baslik}. Düzenlemek için açın`}
          className="w-full text-left px-3 py-3 flex items-start gap-3 min-h-16 rounded-2xl active:bg-[#faf7f2] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2e2d29]">
          <span className="mt-0.5 h-7 w-7 shrink-0 rounded-full bg-[#2e2d29]/10 text-sm font-semibold text-[#2e2d29] flex items-center justify-center tabular-nums">
            {sira}
          </span>
          <span className="min-w-0 flex-1">
            <span className="flex flex-wrap items-center gap-1 mb-1">
              <span className={`rounded-full px-2 py-0.5 text-xs ${r.tur === 'poz' ? 'bg-[#2e2d29]/10 text-[#2e2d29]' : 'bg-[#7a6a2e]/15 text-[#5c4f22]'}`}>
                {r.tur === 'poz' ? 'Sözleşmeli' : 'İlave'}
              </span>
              {devam && <span className="rounded-full px-2 py-0.5 text-xs bg-[#2e6fa0]/10 text-[#24557a]">Devam · {devam.gun_no}. gün</span>}
              {r.tamamlandi && (
                <span className="inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-xs bg-[#3d6e4e]/15 text-[#2d5a3c]">
                  <Check size={12} /> Tamamlandı
                </span>
              )}
            </span>
            <span className={`block text-base leading-snug break-words ${bosBaslik ? 'text-[#8a7e70] italic' : 'font-medium text-[#1a1916]'}`}>
              {baslik}
            </span>
            {meta.length > 0 && <span className="block text-sm text-[#5c554b] mt-0.5 break-words">{meta.join(' · ')}</span>}
            {hata && (
              <span className="flex items-start gap-1 text-sm font-medium text-[#b4442f] mt-1">
                <AlertCircle size={14} className="mt-0.5 shrink-0" /> {hata}
              </span>
            )}
          </span>
          <span className="shrink-0 flex flex-col items-end gap-1.5 pt-0.5">
            <span className="flex items-center gap-1 text-sm font-semibold tabular-nums text-[#2e2d29]">
              <Users size={14} /> {kisiSayisi(r.ekip)}
            </span>
            <ChevronDown size={18} className="text-[#8a7e70]" />
          </span>
        </button>
      </section>
    );
  };

  const isKarti = (r, sira) => (acikIs === r.line_id ? acikKart(r, sira) : ozetSatiri(r, sira));

  const acikKart = (r, sira) => {
    const k = kalemHaritasi[r.kalem_id];
    const kayip = r.tur === 'poz' && r.kalem_id && !k;   // kalem pasife alınmış
    const hata = hatalar[r.line_id];
    const birim = r.tur === 'poz' ? (k?.birim || r.birim) : r.birim;
    const devam = r.is_id ? isBilgileri[r.is_id] : null;   // önceki günlerden gelen iş
    const sabit = kilitli || !!devam;                        // devam eden işin tanımı değişmez
    return (
      <section key={r.line_id} id={`is-${r.line_id}`} data-testid={`is-karti-${sira}`}
        className={`rounded-2xl border bg-white shadow-sm scroll-mt-4 ${hata ? 'border-[#b4442f] ring-1 ring-[#b4442f]/30' : 'border-[#d0c4b0]'}`}>
        <header className="flex items-center justify-between gap-2 px-4 pt-3">
          <p className="text-sm font-semibold text-[#5c554b]">
            {sira}. İş <span className={`ml-1 rounded-full px-2 py-0.5 text-xs ${r.tur === 'poz' ? 'bg-[#2e2d29]/10 text-[#2e2d29]' : 'bg-[#7a6a2e]/15 text-[#5c4f22]'}`}>
              {r.tur === 'poz' ? 'Sözleşmeli' : 'İlave'}</span>
            {devam && (
              <span className="ml-1 rounded-full px-2 py-0.5 text-xs bg-[#2e6fa0]/10 text-[#24557a]" data-testid={`devam-rozet-${sira}`}>
                Devam · {devam.gun_no}. gün
              </span>
            )}
          </p>
          <span className="flex items-center -mr-2">
            {!kilitli && (
              <button type="button" onClick={() => isSil(r.line_id)} aria-label={`${sira}. işi sil`}
                title="İşi sil"
                className="h-11 w-11 flex items-center justify-center rounded-full text-[#8a7e70] active:bg-[#f5f0e8]">
                <Trash2 size={18} />
              </button>
            )}
            <button type="button" onClick={() => kartiKapat(r.line_id)} aria-label={`${sira}. işi kapat`}
              aria-expanded="true"
              className="h-11 w-11 flex items-center justify-center rounded-full text-[#5c554b] active:bg-[#f5f0e8]">
              <ChevronUp size={20} />
            </button>
          </span>
        </header>

        <div className="px-4 pb-4 space-y-4">
          {devam && (
            <p className="flex items-start gap-1.5 text-sm text-[#24557a] bg-[#2e6fa0]/5 rounded-lg px-3 py-2">
              <CalendarClock size={16} className="mt-0.5 shrink-0" />
              <span>
                {tarihMetni(devam.baslangic)} tarihinde başladı · şimdiye kadar {devam.onceki_adam_gun || 0} adam·gün
                {devam.onceki_miktar > 0 && ` · ${devam.onceki_miktar} ${birim || ''} girildi`}
              </span>
            </p>
          )}
          {r.tur === 'poz' ? (
            <div>
              <span className={ETIKET}>İş kalemi (poz) *</span>
              <PozSecici kalemler={isKalemleri} value={kayip ? '' : r.kalem_id} disabled={sabit} sira={sira}
                onChange={(v) => isGuncelle(r.line_id, { kalem_id: v })}
                kayipMetni={kayip ? `${r.poz_no || ''} ${r.is_cinsi || ''}`.trim() : null}
                testId={`poz-sec-${sira}`} />
            </div>
          ) : (
            <>
              <div>
                <label className={ETIKET} htmlFor={`tanim-${r.line_id}`}>İş tanımı *</label>
                <Input id={`tanim-${r.line_id}`} disabled={sabit} value={r.is_cinsi} className={ALAN}
                  onChange={(e) => isGuncelle(r.line_id, { is_cinsi: e.target.value })} data-testid={`ilave-is-${sira}`} />
              </div>
              <div>
                <span className={ETIKET}>İş grubu *</span>
                <MasrafGrubuSecici gruplar={masrafGruplari} value={r.masraf_grubu_id} disabled={sabit}
                  hatali={hata === 'İşin grubunu seçin'} baslik={`${sira}. iş — iş grubunu seçin`}
                  onChange={(v) => isGuncelle(r.line_id, { masraf_grubu_id: v })} testId={`masraf-sec-${sira}`} />
              </div>
            </>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={ETIKET} htmlFor={`miktar-${r.line_id}`}>
                {devam ? 'Bugün yapılan' : 'Miktar'}{r.tur === 'poz' && r.tamamlandi && !(devam?.onceki_miktar > 0) ? ' *' : ''}
                {!r.tamamlandi && <span className="font-normal text-[#8a7e70]"> (bitince)</span>}
              </label>
              <div className="relative">
                <Input id={`miktar-${r.line_id}`} type="number" inputMode="decimal" step="any" min="0" disabled={kilitli}
                  value={r.miktar} onChange={(e) => isGuncelle(r.line_id, { miktar: e.target.value })}
                  className={`${ALAN} tabular-nums ${r.tur === 'poz' && birim ? 'pr-14' : ''}`} data-testid={`miktar-${sira}`} />
                {r.tur === 'poz' && birim && (
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-[#8a7e70]">{birim}</span>
                )}
              </div>
            </div>
            {r.tur !== 'poz' && (
              <div>
                <label className={ETIKET} htmlFor={`birim-${r.line_id}`}>Birim</label>
                <Input id={`birim-${r.line_id}`} disabled={kilitli} value={r.birim} placeholder="m2, adet…"
                  onChange={(e) => isGuncelle(r.line_id, { birim: e.target.value })} className={ALAN} />
              </div>
            )}
          </div>

          {siteKonumlari.length > 0 && (
            <div>
              <span className={ETIKET}>Konum</span>
              <KonumSecici konumlar={siteKonumlari} value={r.konum_id} yol={r.konum_yolu}
                disabled={sabit} onChange={(v) => isGuncelle(r.line_id, { konum_id: v })}
                testId={`konum-${sira}`} />
            </div>
          )}

          {yapanVar && (
            <div>
              <label className={ETIKET} htmlFor={`yapan-${r.line_id}`}>Yapan</label>
              <select id={`yapan-${r.line_id}`} value={r.alt_taseron_id || ''} disabled={kilitli}
                onChange={(e) => isGuncelle(r.line_id, { alt_taseron_id: e.target.value || null })}
                className="w-full h-12 px-3 rounded-md border border-[#d0c4b0] bg-white text-base text-[#1a1916] disabled:opacity-60">
                <option value="">{subcontractorName} (kendimiz)</option>
                {r.alt_taseron_id && !altTaseronlar.some((a) => a.id === r.alt_taseron_id) && (
                  <option value={r.alt_taseron_id} disabled>{r.alt_taseron_adi} (pasif — yenisini seçin)</option>
                )}
                {altTaseronlar.map((a) => <option key={a.id} value={a.id}>{a.ad}</option>)}
              </select>
            </div>
          )}

          {/* EKİP: bu işte hangi nitelikte kaç kişi çalıştı */}
          <div className="rounded-xl bg-[#faf7f2] border border-[#e8dfd0] p-3">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold text-[#3F3E39] flex items-center gap-1.5">
                <Users size={16} /> Çalışan ekip *
              </p>
              <span className="text-sm font-semibold tabular-nums text-[#2e2d29]">{kisiSayisi(r.ekip)} kişi</span>
            </div>
            <div className="space-y-2">
              {(r.ekip || []).map((e, j) => (
                <div key={e.id} className="flex items-center gap-2">
                  {/* Hazır düğmeyle gelen pozisyon düz yazı (dar ekranda kutu kesiyordu);
                      yazı kutusu yalnız "Diğer" için */}
                  {e.ozel ? (
                    <Input value={e.pozisyon} disabled={kilitli} list="pozisyon-onerileri" placeholder="Pozisyon yazın"
                      aria-label="Pozisyon" data-testid={`ekip-poz-${sira}-${j}`} autoFocus={!e.pozisyon}
                      onChange={(ev) => ekipGuncelle(r.line_id, (ekip) => ekip.map((x) => (x.id === e.id ? { ...x, pozisyon: ev.target.value } : x)))}
                      className={`${ALAN} min-w-0 flex-1`} />
                  ) : (
                    <span className="min-w-0 flex-1 text-base font-medium text-[#1a1916] break-words" data-testid={`ekip-poz-${sira}-${j}`}>
                      {e.pozisyon}
                    </span>
                  )}
                  <Stepper deger={e.adet} disabled={kilitli} testId={`ekip-adet-${sira}-${j}`}
                    onChange={(v) => ekipGuncelle(r.line_id, (ekip) => ekip.map((x) => (x.id === e.id ? { ...x, adet: v } : x)))} />
                  {!kilitli && (
                    <button type="button" aria-label={`${e.pozisyon || 'satırı'} çıkar`}
                      onClick={() => ekipGuncelle(r.line_id, (ekip) => ekip.filter((x) => x.id !== e.id))}
                      className="h-11 w-9 flex items-center justify-center text-[#8a7e70] active:text-[#7a3535] shrink-0">
                      <X size={18} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <label className="mt-3 flex items-center gap-2 text-sm text-[#3F3E39]">
              <Clock size={16} className="shrink-0 text-[#5c554b]" />
              <span className="flex-1">Bugün kaç saat çalışıldı?</span>
              <Input type="number" inputMode="decimal" step="0.5" min="0" max="24" value={r.saat ?? ''} disabled={kilitli}
                onChange={(e) => isGuncelle(r.line_id, { saat: e.target.value })} aria-label="Çalışma saati"
                className="h-11 w-20 text-base text-right tabular-nums bg-white border-[#d0c4b0]" data-testid={`saat-${sira}`} />
              <span>saat</span>
            </label>
            {!kilitli && (
              <div className="mt-2 flex flex-wrap gap-2">
                {POZISYONLAR.map((p) => (
                  <button key={p} type="button" onClick={() => pozisyonEkle(r.line_id, p)} data-testid={`hizli-${sira}-${p}`}
                    className="h-11 px-3.5 rounded-full border border-[#d0c4b0] bg-white text-sm font-medium text-[#3F3E39] active:bg-[#efe8dc]">
                    + {p}
                  </button>
                ))}
                <button type="button" onClick={() => ekipGuncelle(r.line_id, (ekip) => [...ekip, { id: uid(), pozisyon: '', adet: 1, ozel: true }])}
                  className="h-11 px-3.5 rounded-full border border-dashed border-[#b0a090] text-sm text-[#5c554b] active:bg-[#efe8dc]">
                  + Diğer
                </button>
              </div>
            )}
          </div>

          {(r.aciklama || r.notAcik) ? (
            <div>
              <label className={ETIKET} htmlFor={`not-${r.line_id}`}>Açıklama <span className="font-normal text-[#8a7e70]">(isteğe bağlı)</span></label>
              <Input id={`not-${r.line_id}`} disabled={kilitli} value={r.aciklama || ''} className={ALAN} autoFocus={r.notAcik && !r.aciklama}
                onChange={(e) => isGuncelle(r.line_id, { aciklama: e.target.value })} />
            </div>
          ) : !kilitli && (
            <button type="button" onClick={() => isGuncelle(r.line_id, { notAcik: true })}
              className="h-11 -my-1 text-sm font-medium text-[#5c554b] underline underline-offset-2">
              + Açıklama ekle
            </button>
          )}

          {/* İş bitti mi? İşaretlenmezse yarın formuna kendiliğinden gelir */}
          <label className={`flex items-start gap-3 rounded-xl border p-3 cursor-pointer
            ${r.tamamlandi ? 'border-[#3d6e4e] bg-[#3d6e4e]/10' : 'border-[#d0c4b0] bg-white'}`} data-testid={`tamamlandi-${sira}`}>
            <input type="checkbox" checked={!!r.tamamlandi} disabled={kilitli}
              onChange={(e) => isGuncelle(r.line_id, { tamamlandi: e.target.checked })}
              className="mt-0.5 h-6 w-6 shrink-0 accent-[#3d6e4e]" />
            <span>
              <span className="block text-base font-semibold text-[#1a1916]">Bu iş tamamlandı</span>
              <span className="block text-sm text-[#5c554b]">
                {r.tamamlandi
                  ? (r.tur === 'poz' ? 'İş kapanır. Yapılan toplam miktarı girmeyi unutmayın.' : 'İş bugün kapanır.')
                  : 'İşaretlemezseniz bu iş yarın formunuza kendiliğinden gelir.'}
              </span>
            </span>
          </label>

          {hata && (
            <p role="alert" className="flex items-start gap-1.5 text-sm font-medium text-[#b4442f]">
              <AlertCircle size={16} className="mt-0.5 shrink-0" /> {hata}
            </p>
          )}

          <Button type="button" onClick={() => kartiKapat(r.line_id)} data-testid={`is-tamam-${sira}`}
            className="w-full h-12 text-base bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916]">
            Tamam
          </Button>
        </div>
      </section>
    );
  };

  return (
    <div className="min-h-dvh bg-[#f5f0e8]">
      <datalist id="pozisyon-onerileri">
        {[...POZISYONLAR, 'Şoför', 'Kaynakçı', 'Elektrikçi', 'Tesisatçı'].map((p) => <option key={p} value={p} />)}
      </datalist>

      <div className={`mx-auto ${masaustu ? 'max-w-[1600px] px-6 pt-8 pb-32' : 'max-w-xl px-4 pt-5 pb-40'}`}>
        <div className={masaustu ? 'flex items-center justify-between gap-8 mb-6' : ''}>
        <header className={`flex items-center gap-3 min-w-0 ${masaustu ? '' : 'mb-5'}`}>
          <div className="w-12 h-12 rounded-xl bg-[#2e2d29]/10 flex items-center justify-center shrink-0">
            <HardHat size={24} className="text-[#2e2d29]" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl font-bold text-[#1a1916] leading-tight">{subcontractorName}</h1>
            <p className="text-sm text-[#5c554b]">{ustTaseronAdi ? `${ustTaseronAdi} alt taşeronu · ` : ''}Günlük Rapor</p>
          </div>
        </header>
        {/* Masaüstünde tarih/şantiye başlık satırının sağında */}
        {masaustu && tarihSantiye}
        </div>

        {kilitli && (
          <div className="mb-4 p-3 rounded-xl bg-[#3d6e4e]/10 border border-[#3d6e4e]/30 text-base text-[#2d5a3c]">
            Bu gün için rapor onaylanmış. Değişiklik için ofis ile iletişime geçin.
          </div>
        )}
        {status === 'rejected' && reviewNote && (
          <div role="alert" className="mb-4 p-3 rounded-xl bg-[#7a3535]/5 border border-[#7a3535]/30 text-base text-[#7a3535]">
            Ofis bu raporu geri çevirdi: {reviewNote} — lütfen düzeltip tekrar gönderin.
          </div>
        )}
        {calismaYok && (
          <div className="mb-4 p-3 rounded-xl bg-[#7a6a2e]/10 border border-[#7a6a2e]/30 text-base text-[#5c4f22]" data-testid="calisma-yok-bilgi">
            Bu gün <b>çalışma yok</b> olarak bildirildi. Çalışma olduysa işleri girip yeniden gönderin.
          </div>
        )}

        {!masaustu && tarihSantiye}

        <div className="flex items-baseline justify-between mb-3">
          <h2 className="text-lg font-bold text-[#1a1916]">Yapılan İşler</h2>
          {isler.length > 0 && <span className="text-sm text-[#5c554b] tabular-nums">{isler.length} iş · {toplamKisi} kişi</span>}
        </div>

        {isKalemleri.length === 0 && (
          <p className="mb-3 p-3 rounded-xl bg-[#7a6a2e]/10 text-sm text-[#5c4f22]">
            Size henüz iş kalemi tanımlanmamış. Yaptığınız işleri "İlave İş" olarak ekleyin ya da ofisle görüşün.
          </p>
        )}

        {masaustu ? (
          <IsTablosu isler={isler} kilitli={kilitli} hatalar={hatalar} isBilgileri={isBilgileri}
            kalemHaritasi={kalemHaritasi} isKalemleri={isKalemleri} masrafGruplari={masrafGruplari}
            siteKonumlari={siteKonumlari} yapanVar={yapanVar} altTaseronlar={altTaseronlar}
            subcontractorName={subcontractorName} yeniSatir={acikIs} isGuncelle={isGuncelle} isSil={isSil}
            ekipGuncelle={ekipGuncelle} pozisyonEkle={pozisyonEkle} raporTarihi={reportDate} />
        ) : (
          <div className="space-y-3">
            {isler.map((r, i) => isKarti(r, i + 1))}
          </div>
        )}

        {!kilitli && (
          <div className={masaustu ? 'mt-3 flex flex-wrap items-center gap-3' : 'mt-4 grid gap-3'}>
            {isKalemleri.length > 0 && (
              <Button type="button" onClick={() => isEkle('poz')} data-testid="poz-is-ekle"
                className={`text-base bg-white text-[#1a1916] border border-[#2e2d29] hover:bg-[#faf7f2] ${masaustu ? 'h-11 px-5' : 'h-12'}`}>
                <Plus size={18} className="mr-1.5" /> Sözleşmeli İş Ekle
              </Button>
            )}
            <Button type="button" variant="outline" onClick={() => isEkle('ilave')} data-testid="ilave-is-ekle"
              className={`text-base border-dashed border-[#b0a090] text-[#3F3E39] ${masaustu ? 'h-11 px-5' : 'h-12'}`}>
              <Plus size={18} className="mr-1.5" /> İlave İş Ekle
            </Button>
            <p className="text-sm text-[#8a7e70] px-1">
              İlave iş: size tanımlı iş kalemlerinde olmayan ek işler. Fiyatını ofis belirler.
              {masaustu && ' "Bitti" işaretlenmeyen işler yarınki formunuza kendiliğinden gelir.'}
            </p>
          </div>
        )}

        {bagimsizPuantaj.length > 0 && (
          <p className="mt-4 text-sm text-[#8a7e70]">
            Bu raporda bir işe bağlı olmayan {kisiSayisi(bagimsizPuantaj)} kişilik eski puantaj kaydı var; korunuyor.
          </p>
        )}

        {!kilitli && !calismaYok && !yeniIsVar && (
          <button type="button" onClick={() => handleSubmit(true)} disabled={submitting} data-testid="calisma-yok"
            className={`h-12 rounded-xl text-base text-[#5c554b] underline underline-offset-4 active:bg-[#efe8dc] ${masaustu ? 'mt-6 px-2 hover:text-[#1a1916]' : 'mt-8 w-full'}`}>
            Bugün çalışma yok
          </button>
        )}
      </div>

      {/* Alta yapışık tek ana buton — telefonda başparmak erişiminde */}
      {!kilitli && (
        <div className="fixed inset-x-0 bottom-0 z-20 border-t border-[#d0c4b0] bg-[#faf7f2]/95 backdrop-blur"
          style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
          <div className={`mx-auto py-3 flex items-center ${masaustu ? 'max-w-[1600px] px-6 justify-end gap-6' : 'max-w-xl px-4 gap-3'}`}>
            <div className="text-sm leading-tight text-[#5c554b] tabular-nums shrink-0">
              <b className="text-[#1a1916] text-base">{isler.length}</b> iş<br />
              <b className="text-[#1a1916] text-base">{toplamKisi}</b> kişi
            </div>
            <Button onClick={() => handleSubmit(false)} disabled={submitting} data-testid="rapor-gonder"
              className={`${masaustu ? 'w-72' : 'flex-1'} h-12 text-base font-semibold bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916]`}>
              {submitting ? 'Gönderiliyor…' : 'Raporu Gönder'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
